import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useId, useState } from "react";

interface FigmaImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The design's name when known, else its link. */
  targetLabel: string;
  onImport: (options: { includeVisuals: boolean }) => void;
}

type VisualMode = "text" | "visuals";

/**
 * CR-126: the import choice. Before anything is copied, the member is told the
 * imported content becomes readable by every project member (even without
 * Figma access) and that comments are included. With visual previews, up to
 * 8 frames are rendered by Figma and stored in Kairo as images.
 */
export function FigmaImportDialog({
  open,
  onOpenChange,
  targetLabel,
  onImport,
}: FigmaImportDialogProps) {
  const [mode, setMode] = useState<VisualMode>("text");
  const textId = useId();
  const visualsId = useId();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="figma-import-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Import Figma design context</DialogTitle>
          <DialogDescription className="break-all">{targetLabel}</DialogDescription>
        </DialogHeader>
        <p
          data-testid="figma-import-disclosure"
          className="bg-muted text-muted-foreground rounded-md p-3 text-sm"
        >
          Kairo copies the design text, structure, published components and comments into a document
          every project member can read, even without Figma access.
        </p>
        <RadioGroup
          value={mode}
          onValueChange={(value) => setMode(value === "visuals" ? "visuals" : "text")}
          className="gap-3"
        >
          <div className="flex items-start gap-2">
            <RadioGroupItem value="text" id={textId} data-testid="figma-import-mode-text" />
            <Label htmlFor={textId} className="leading-snug font-normal">
              Text only
            </Label>
          </div>
          <div className="flex items-start gap-2">
            <RadioGroupItem
              value="visuals"
              id={visualsId}
              data-testid="figma-import-mode-visuals"
            />
            <Label
              htmlFor={visualsId}
              className="flex flex-col items-start gap-0.5 leading-snug font-normal"
            >
              <span>Text and visual previews</span>
              {/* 8 = MAX_VISUAL_FRAMES in backend/api-server/src/lib/figma/figma-visuals.ts: keep in sync. */}
              <span className="text-muted-foreground text-xs">
                Up to 8 frames, stored as images in the document.
              </span>
            </Label>
          </div>
        </RadioGroup>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            data-testid="figma-import-confirm"
            onClick={() => {
              onImport({ includeVisuals: mode === "visuals" });
              onOpenChange(false);
            }}
          >
            Import
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
